import { type PointerEvent, useCallback, useRef } from "react";
import { OrthographicCamera, Vector3 } from "three";

import { useCamera } from "../../contexts/camera";
import {
  CAMERA_GIZMO_RADIANS_PER_DRAG_ACROSS,
  CAMERA_GIZMO_SCREEN_HEIGHTS_PER_DRAG_ACROSS
} from "../../game/gameConstants";
import { useModelInstance } from "../../render/hooks/useModelInstance";
import { useModelScene } from "../../render/hooks/useModelScene";
import { SecondaryRenderCanvas } from "../shared/SecondaryRenderCanvas";
import "./CameraGizmo.css";

type CameraGizmoPipAxis = "yaw" | "pitch";

type CameraGizmoDragKind = CameraGizmoPipAxis | "pan";

const CAMERA_GIZMO_PIPS: { nodeName: string; turns: CameraGizmoPipAxis }[] = [
  { nodeName: "H_Pip_1", turns: "yaw" },
  { nodeName: "H_Pip_2", turns: "yaw" },
  { nodeName: "V_Pip_1", turns: "pitch" },
  { nodeName: "V_Pip_2", turns: "pitch" }
];

const CAMERA_GIZMO_PAN_TARGET_NODE_NAME = "Figure";

type CameraGizmoDrag = {
  pointerId: number;
  lastClientX: number;
  lastClientY: number;
  kind: CameraGizmoDragKind;
};

const scratchVector = new Vector3();

export function CameraGizmo() {
  const model = useModelInstance("/models/ui/camera-gizmo.glb");
  const { scene, camera } = useModelScene(model);
  const [cameraCtx] = useCamera();
  const gizmoRef = useRef<HTMLDivElement>(null);
  const pipTargetRefs = useRef<(HTMLDivElement | null)[]>([]);
  const panTargetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<CameraGizmoDrag | null>(null);

  const followBoardCamera = useCallback(() => {
    const boardCameraRotation = cameraCtx.worldRotation;
    if (!model || !scene || !camera || !boardCameraRotation) return;

    model.quaternion
      .copy(boardCameraRotation)
      .invert()
      .premultiply(camera.quaternion);

    model.updateMatrixWorld();

    CAMERA_GIZMO_PIPS.forEach((pip, index) => {
      const pipNode = scene.getObjectByName(pip.nodeName);
      const target = pipTargetRefs.current[index];
      if (!pipNode || !target) return;

      const ndc = pipNode.getWorldPosition(scratchVector).project(camera);

      target.style.left = `${(ndc.x + 1) * 50}%`;
      target.style.top = `${(1 - ndc.y) * 50}%`;
    });

    const panNode = scene.getObjectByName(CAMERA_GIZMO_PAN_TARGET_NODE_NAME);
    const panTarget = panTargetRef.current;
    if (panNode && panTarget) {
      const ndc = panNode.getWorldPosition(scratchVector).project(camera);

      panTarget.style.left = `${(ndc.x + 1) * 50}%`;
      panTarget.style.top = `${(1 - ndc.y) * 50}%`;
    }
  }, [cameraCtx, model, scene, camera]);

  const startDrag = useCallback(
    (event: PointerEvent<HTMLDivElement>, kind: CameraGizmoDragKind) => {
      if (!event.isPrimary || event.button !== 0) return;

      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
        kind
      };
    },
    []
  );

  const continueDrag = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;

      const deltaX = drag.lastClientX - event.clientX;
      const deltaY = drag.lastClientY - event.clientY;
      drag.lastClientX = event.clientX;
      drag.lastClientY = event.clientY;

      const gizmoHeight = gizmoRef.current?.clientHeight;
      const boardCamera = cameraCtx.boardCamera;
      if (!gizmoHeight || !boardCamera) return;

      const { controls } = boardCamera;

      if (drag.kind === "pan") {
        const camera = boardCamera.camera;
        if (!(camera instanceof OrthographicCamera)) return;

        const screenHeightInWorldUnits =
          (camera.top - camera.bottom) / camera.zoom;
        const worldUnitsPerPixel =
          (CAMERA_GIZMO_SCREEN_HEIGHTS_PER_DRAG_ACROSS *
            screenHeightInWorldUnits) /
          gizmoHeight;

        controls.truck(
          deltaX * worldUnitsPerPixel,
          deltaY * worldUnitsPerPixel,
          true
        );
      } else {
        const radiansPerPixel =
          CAMERA_GIZMO_RADIANS_PER_DRAG_ACROSS / gizmoHeight;

        if (drag.kind === "yaw") {
          controls.rotate(deltaX * radiansPerPixel, 0, true);
        } else {
          controls.rotate(0, deltaY * radiansPerPixel, true);
        }
      }
    },
    [cameraCtx]
  );

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  }, []);

  return (
    <div ref={gizmoRef} className="camera-gizmo">
      <SecondaryRenderCanvas
        scene={scene}
        camera={camera}
        onFrame={followBoardCamera}
      />
      <div
        ref={panTargetRef}
        className="camera-gizmo-pan-target"
        onPointerDown={(event) => startDrag(event, "pan")}
        onPointerMove={continueDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
      {CAMERA_GIZMO_PIPS.map((pip, index) => (
        <div
          key={pip.nodeName}
          ref={(element) => {
            pipTargetRefs.current[index] = element;
          }}
          className="camera-gizmo-pip-target"
          onPointerDown={(event) => startDrag(event, pip.turns)}
          onPointerMove={continueDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        />
      ))}
    </div>
  );
}
